import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Bot, X, Search } from "lucide-react";
import { motion, Variants } from "framer-motion";
import { getMessageDisplayText, getMessageHeader, formatWhatsAppText } from "@/utils/message-parser";
import { ASSET_BASE } from "@/lib/asset-base";
import { MessengerConfig, messengerLink } from "@/lib/messenger";
import {
  QuickActionItem,
  QuickActionsConfig,
  TeamAvatarsConfig,
  HomeCopyConfig,
  HomeCardsConfig,
  HomeDesignConfig,
} from "@/lib/quick-actions";
import { useGrainBackdrop, grainGradientCss } from "@/components/FilmGrain";

interface ChatHomeProps {
  onNewChat: () => void;
  onPreviousChats: () => void;
  onViewOrders?: () => void;
  onClose?: () => void;
  headerText?: string;
  subheaderText?: string;
  preloginHeaderText?: string;
  preloginSubheaderText?: string;
  theme: "light" | "dark";
  customColors?: {
    backgroundColor?: string;
    textColor?: string;
    secondaryTextColor?: string;
    borderColor?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
    /** Home-screen header + subheader colour. Undefined = the legacy white
     *  (these two lines were hardcoded `text-white` before this existed), so
     *  widgets that never set it are unchanged. */
    headerTextColor?: string;
    /** "Powered by flowapt.com" footer colour. Undefined = the legacy
     *  `text-black`, so widgets that never set it are unchanged. */
    footerTextColor?: string;
    /** Colour of the home screen's top-down gradient. Undefined = the accent
     *  (buttonBackgroundColor), the legacy look, so widgets that never set it
     *  are unchanged. Lets a store keep a bright accent on the buttons only. */
    homeBackgroundColor?: string;
  };
  customIcons?: {
    userWeb?: React.ReactNode;
    botWeb?: React.ReactNode;
    humanWeb?: React.ReactNode;
  };
  contactId?: string | null;
  organizationId: string;
  userInfo?: {
    fullName?: string;
    email?: string;
    phone?: string;
  } | null;
  recentMessage?: {
    message: string;
    senderName: string;
    createdAt: string;
    senderType: string;
  } | null;
  whatsapp?: { number: string; prefill_text?: string } | null;
  /** Facebook Page the "Chat on Messenger" card opens (m.me). Null = no card. */
  messenger?: MessengerConfig | null;
  iconDisplay?: { userRaw?: boolean; userLarge?: boolean; botRaw?: boolean; humanRaw?: boolean };
  /** Quick actions ("Search for help" card): chat / info-article / WhatsApp /
   *  link rows, configured per widget. Null/absent = card hidden. */
  quickActions?: QuickActionsConfig | null;
  /** Signed-in store customer id from the storefront, or absent for a visitor. */
  shopifyCustomerId?: string | null;
  /** Signed-in store customer's first name (server-rendered by the Shopify app
   *  embed). Replaces `{first_name}` in the header / subheader copy; when the
   *  name is unknown the token and its leading ", " / " " are removed. */
  shopifyCustomerFirstName?: string | null;
  onQuickAction?: (item: QuickActionItem) => void;
  /** Opens the Help page (the ?-icon view). The home search box no longer
   *  types in place — clicking it goes there, like Intercom. */
  onOpenHelp?: () => void;
  /** Opens the in-widget subscription manager (shown only when an engine is enabled). */
  onOpenManage?: () => void;
  /** Opens the rewards panel (shown only when the org runs a Passes loyalty
   *  programme AND the widget's rewards config is enabled). */
  onOpenRewards?: () => void;
  /** Per-widget rewards config — supplies the card's title/subtitle copy. */
  rewardsConfig?: { card_title?: string; card_subtitle?: string } | null;
  /** Intercom-style people circles beside the logo. Null/absent = hidden. */
  teamAvatars?: TeamAvatarsConfig | null;
  /** Editable home-card labels; absent fields use the built-in defaults. */
  homeCopy?: HomeCopyConfig | null;
  /** Built-in home-card visibility; chat:false hides the web-chat entry. */
  homeCards?: HomeCardsConfig | null;
  /** Home-screen design: greeting/subheader sizes, alignment and the intro
   *  paragraph with its style. Null/absent = the legacy rendering. */
  homeDesign?: HomeDesignConfig | null;
  /** Content-height welcome card; the parent controls viewport clamping. */
  compact?: boolean;
  /** Visitor-facing agent name — the sender line of the intro bubble. */
  agentName?: string;
  enableAnimations?: boolean;
}

/** Initial-circle backgrounds when a person has no image and no colour. */
const AVATAR_PALETTE = ["#C9A7D8", "#A7C7E7", "#F4C7A1", "#9FD8C9", "#E7A7B8"];
/** Cluster shows at most this many circles; the rest collapse into "+N". */
const AVATAR_MAX = 5;

// Smooth ease-out curve (expo-out) — the one Intercom-style UIs use for entrances
const EASE_OUT = [0.16, 1, 0.3, 1] as const;

// Parent container — staggers its children in sequence
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: EASE_OUT as any,
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

// Each child — small upward slide with fade. No scale, no bounce.
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: EASE_OUT as any,
    },
  },
};

/**
 * Fill the `{first_name}` token in greeting copy with the signed-in store
 * customer's name. "Olá, {first_name} 😊" → "Olá, Matt 😊"; with no name the
 * token AND its leading ", " / " " go, leaving "Olá 😊". Copy without the
 * token is returned untouched, so every existing widget renders as before.
 */
export function greet(text: string, firstName?: string | null): string {
  if (!text || !text.includes("{first_name}")) return text;
  const name = (firstName ?? "").trim();
  if (name) return text.replace(/\{first_name\}/g, name);
  return text
    .replace(/(,\s*|\s+)?\{first_name\}/g, "")
    .replace(/^\s*,\s*/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const ChatHome: React.FC<ChatHomeProps> = ({
  onPreviousChats,
  onViewOrders,
  onClose,
  headerText,
  subheaderText,
  preloginHeaderText,
  preloginSubheaderText,
  customColors,
  customIcons,
  contactId,
  organizationId,
  userInfo,
  recentMessage = null,
  whatsapp,
  messenger,
  iconDisplay,
  quickActions,
  shopifyCustomerId,
  shopifyCustomerFirstName,
  onQuickAction,
  onOpenHelp,
  onOpenManage,
  onOpenRewards,
  rewardsConfig,
  teamAvatars,
  homeCopy,
  homeCards,
  homeDesign,
  compact = false,
  agentName,
  enableAnimations = true,
}) => {
  // chat:false removes the web-chat entry from the home screen entirely —
  // widgets that route all support through topic buttons (WhatsApp/email).
  const chatEntryHidden = homeCards?.chat === false;
  const ordersCardHidden = homeCards?.orders === false;
  // Channel marks are flowapt.com's own channel icons (public/channel-icons
  // on the website, copied into widget-assets on 30 Oct 2026), served from
  // the widget host so no storefront depends on a third-party CDN.
  const whatsappIcon = `${ASSET_BASE}/widget-assets/whatsapp.webp`;
  const messengerIcon = `${ASSET_BASE}/widget-assets/messenger.webp`;
  const messengerHref = messengerLink(messenger);
  // Person cards that open WhatsApp ("Speak to Jan") wear the WhatsApp mark
  // before the label, vertically centred on the text by the flex row it sits
  // in. Text-only WhatsApp shortcuts are unchanged.
  const waMarkFor = (item: QuickActionItem, size = "h-4 w-4") =>
    item.action === "whatsapp" && item.image_url ? (
      <img
        src={whatsappIcon}
        alt="WhatsApp"
        className={`${size} flex-shrink-0 object-contain`}
      />
    ) : null;
  const primaryColor = customColors?.buttonBackgroundColor || "#2563eb";

  // Home-card labels — per-widget copy with the historical strings as defaults.
  const txt = (v: string | undefined, fallback: string) =>
    (v || "").trim() || fallback;
  const copyRecentTitle = txt(homeCopy?.recent_title, "Recent message");
  const copySendTitle = txt(homeCopy?.send_title, "Send us a message");
  const copySendSubtitle = txt(homeCopy?.send_subtitle, "Get instant replies");
  const copyOrdersTitle = txt(homeCopy?.orders_title, "My Orders");
  const copyOrdersSubtitle = txt(
    homeCopy?.orders_subtitle,
    "View your order history"
  );
  const copyWhatsappLabel = txt(homeCopy?.whatsapp_label, "Chat on WhatsApp");
  const copyWhatsappSublabel = (homeCopy?.whatsapp_sublabel || "").trim();
  const copyMessengerLabel = txt(homeCopy?.messenger_label, "Chat on Messenger");
  const copyMessengerSublabel = (homeCopy?.messenger_sublabel || "").trim();
  const copyManageLabel = txt(homeCopy?.manage_subs_label, "Manage my subscription");
  const copyManageSublabel = txt(homeCopy?.manage_subs_sublabel, "Pause, edit products or move a delivery");
  const copyRewardsLabel = txt(rewardsConfig?.card_title, "My rewards");
  const copyRewardsSublabel = txt(rewardsConfig?.card_subtitle, "Points, rewards and referrals");

  // ── Card material — one depth system for every home tile ──────────────────
  // The fill is a LITERAL gradient, not from-*/to-* utilities: those ride the
  // --tw-gradient-* variables, and a host page on Tailwind v4 registers them
  // document-wide (shadow root included) as <color>, which makes v3's values
  // invalid and the cards transparent (flowapt.com, 29 Sep 2026).
  // Modeled surface (subtle top-light gradient + inset highlight), hairline
  // edge, and a 3-layer shadow with real offsets: contact, ambient, and a deep
  // soft drop. Motion (lift on hover, compress on tap) is driven by framer so
  // it composes with the entrance animation; box-shadow transitions in CSS.
  const CARD_SHADOW =
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_1px_2px_rgba(16,24,40,0.06),0_4px_10px_-2px_rgba(16,24,40,0.08),0_16px_32px_-16px_rgba(16,24,40,0.26)]";
  const CARD_SHADOW_HOVER =
    "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_2px_4px_rgba(16,24,40,0.07),0_8px_18px_-4px_rgba(16,24,40,0.12),0_26px_48px_-20px_rgba(16,24,40,0.34)]";
  const CARD =
    `w-full bg-[linear-gradient(180deg,#fff,#f7f8fa)] rounded-2xl ring-1 ring-black/[0.05] ${CARD_SHADOW} ${CARD_SHADOW_HOVER} transition-shadow duration-200 ease-out`;
  const cardMotion = {
    whileHover: { y: -2 },
    whileTap: { scale: 0.985, y: 0 },
    transition: { type: "spring", stiffness: 500, damping: 32 } as const,
  };

  // Team avatar cluster (beside the logo). People need an image or a name.
  const avatarPeople = (teamAvatars?.people || []).filter(
    (p) => p && (p.image_url || (p.name || "").trim())
  );
  const showTeamAvatars =
    teamAvatars?.enabled !== false && avatarPeople.length > 0;
  const avatarsOnLeft = teamAvatars?.position === "left";
  const visiblePeople = avatarPeople.slice(0, AVATAR_MAX);
  const hiddenPeopleCount = avatarPeople.length - visiblePeople.length;
  const avatarCluster = showTeamAvatars ? (
    <div className="flex items-center shrink-0" aria-label="Our team">
      {visiblePeople.map((p, i) => (
        <div
          key={p.id || i}
          className={`h-12 w-12 rounded-full overflow-hidden ring-2 ring-white/80 flex items-center justify-center ${
            i > 0 ? "-ml-3" : ""
          }`}
          style={{
            backgroundColor: p.image_url
              ? "#FFFFFF"
              : p.color || AVATAR_PALETTE[i % AVATAR_PALETTE.length],
            zIndex: visiblePeople.length - i,
          }}
        >
          {p.image_url ? (
            <img
              src={p.image_url}
              alt={p.name || ""}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-lg font-semibold text-gray-800">
              {(p.name || "?").trim().charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      ))}
      {hiddenPeopleCount > 0 && (
        <div className="h-12 w-12 rounded-full ring-2 ring-white/80 -ml-3 bg-white/85 flex items-center justify-center text-[13px] font-semibold text-gray-700">
          +{hiddenPeopleCount}
        </div>
      )}
    </div>
  ) : null;

  // Quick actions ("Search for help" card). Typing/search moved to the Help
  // page — the box here is an OPENER for it (Intercom pattern).
  // An item with no `audience` shows to everyone (every widget before 11 Sep
  // 2026). "logged_in" needs the storefront to report a customer; "logged_out"
  // is the lead case — the shortcuts that exist to convert a visitor.
  const signedIn = !!(shopifyCustomerId && String(shopifyCustomerId).trim());
  const quickItems = useMemo(
    () =>
      (quickActions?.items || []).filter((i) => {
        if (!i || typeof i.label !== "string" || !i.label.trim()) return false;
        const aud = i.audience;
        if (!aud || aud === "all") return true;
        return aud === "logged_in" ? signedIn : !signedIn;
      }),
    [quickActions, signedIn]
  );
  const showQuickActions =
    quickActions?.enabled !== false && quickItems.length > 0 && !!onQuickAction;
  const showHelpSearch = quickActions?.search !== false && !!onOpenHelp;
  const quickTitle = (quickActions?.title || "").trim() || "Search for help";
  // Optional line above the shortcut cards (buttons / grid modes only).
  const quickHeading = (quickActions?.heading || "").trim();
  // "buttons" renders every quick action as its own prominent home card
  // (topic-button style); "grid" packs them into a two-column mosaic of
  // compact cards; absent/"list" keeps the classic rows-in-one-card.
  const quickAsGrid = quickActions?.display === "grid";
  const quickAsButtons = quickActions?.display === "buttons" || quickAsGrid;
  // Undefined leaves the `text-white` class to win, so every widget that hasn't
  // set a header colour renders exactly as before.
  const headerTextColor = customColors?.headerTextColor;
  // Same rule for the footer: undefined keeps `text-black`, so only a widget
  // that sets footer_text_color (e.g. a dark home screen) changes.
  const footerTextColor = compact ? "#4B5563" : customColors?.footerTextColor;
  // The home gradient used to be the accent, always. A widget that sets
  // home_background_color (e.g. a dark home with yellow buttons) gets that
  // instead; undefined keeps the accent, so nothing else moves.
  const homeColor = customColors?.homeBackgroundColor || primaryColor;
  // What the home gradient fades into: the panel colour behind it (ChatWidget
  // paints the container customColors.backgroundColor on home, else white).
  // Painted under the gradient so the film grain has a solid backdrop.
  const panelColor = customColors?.backgroundColor || "#FFFFFF";
  // The brand surfaces' grained backgrounds: one opaque image each, gradient
  // and grain already combined, so the compositor sees a plain background
  // (see FilmGrain). Null until rendered; the CSS gradient stands in.
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const homeBackdrop = useGrainBackdrop(rootRef, { enabled: !compact, homeColor, panelColor, gradient: true });
  const headerBackdrop = useGrainBackdrop(headerRef, { enabled: !!compact, homeColor, panelColor: homeColor, gradient: false });
  const isLoggedIn = !!(contactId && userInfo?.fullName);
  const firstName = userInfo?.fullName?.split(" ")[0] || "there";

  // ── Home design (web_chat_widgets.home_design) ─────────────────────────────
  // NULL/absent = the legacy rendering, class for class, so widgets that never
  // set it are byte-identical. Sizes are named steps, never free values.
  const design: HomeDesignConfig = homeDesign || {};
  const centered = design.align === "center";
  const greetingSize = design.greeting_size || "lg";
  const taglineSize = design.tagline_size || "lg";
  const taglineWeight = design.tagline_weight || "normal";
  const greetingClass =
    greetingSize === "sm"
      ? "text-[22px] leading-[1.2] tracking-[-0.01em]"
      : greetingSize === "md"
        ? "text-[26px] leading-[1.15] tracking-[-0.01em]"
        : "text-3xl";
  const taglineClass = `${
    taglineSize === "sm"
      ? "text-[15px] leading-snug"
      : taglineSize === "md"
        ? "text-[17px] leading-snug"
        : "text-xl"
  } ${
    taglineWeight === "semibold"
      ? "font-semibold"
      : taglineWeight === "medium"
        ? "font-medium"
        : "font-normal"
  }`;
  // Intro paragraph: the longer welcome copy gets its own body tier (and,
  // optionally, a white surface or the agent's speech bubble) so the subheader
  // can stay a one-liner. Audience gates it to first or returning visits.
  const introText = (design.intro_text || "").trim();
  const introAudience = design.intro_audience || "all";
  const showIntro =
    !!introText &&
    (introAudience === "all" ||
      (introAudience === "first_visit" ? !isLoggedIn : isLoggedIn));
  const introStyle = design.intro_style || "text";
  const introOnSurface = introStyle === "card" || introStyle === "bubble";
  const introSizeClass =
    design.intro_size === "sm"
      ? "text-[14px] leading-[1.55]"
      : "text-[15px] leading-[1.55]";
  const introClampLines = Math.max(
    0,
    Math.floor(Number(design.intro_clamp_lines) || 0)
  );
  const introReadMore =
    (design.intro_read_more_label || "").trim() || "Read more";
  const introReadLess =
    (design.intro_read_less_label || "").trim() || "Show less";
  const [introExpanded, setIntroExpanded] = useState(false);
  const [introOverflows, setIntroOverflows] = useState(false);
  const introRef = useRef<HTMLParagraphElement>(null);
  const introClamped = introClampLines > 0 && !introExpanded;
  // The Read more toggle only appears when the clamp actually hides text —
  // measured, not assumed, so short copy never grows a pointless button.
  useEffect(() => {
    if (!showIntro || introClampLines === 0 || introExpanded) return;
    const el = introRef.current;
    if (!el) return;
    const measure = () =>
      setIntroOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [showIntro, introClampLines, introExpanded, introText]);

  const renderIntroBody = () => (
    <>
      <p
        ref={introRef}
        className={`${introSizeClass} whitespace-pre-line ${
          introOnSurface ? "text-gray-800" : "opacity-[0.82]"
        }`}
        style={
          introClamped
            ? {
                display: "-webkit-box",
                WebkitLineClamp: introClampLines,
                WebkitBoxOrient: "vertical" as const,
                overflow: "hidden",
              }
            : undefined
        }
      >
        {greet(introText, shopifyCustomerFirstName)}
      </p>
      {introClampLines > 0 && (introOverflows || introExpanded) && (
        <button
          type="button"
          onClick={() => setIntroExpanded((v) => !v)}
          aria-expanded={introExpanded}
          className={`mt-1.5 text-[13px] font-semibold underline decoration-1 underline-offset-[3px] transition-opacity hover:opacity-75 ${
            introOnSurface ? "text-gray-900" : ""
          }`}
        >
          {introExpanded ? introReadLess : introReadMore}
        </button>
      )}
    </>
  );
  // Speech-bubble avatar: the widget's bot icon when it has one, else the
  // agent's initial on a white disc (the same fallback the team cluster uses).
  const agentLabel = (agentName || "").trim() || "AI Agent";
  const introAvatar = customIcons?.botWeb ? (
    <div className="h-8 w-8 rounded-full overflow-hidden bg-white ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(16,24,40,0.08),0_4px_10px_-4px_rgba(16,24,40,0.28)] flex items-center justify-center [&>img]:h-full [&>img]:w-full [&>img]:object-cover">
      {customIcons.botWeb}
    </div>
  ) : (
    <div className="h-8 w-8 rounded-full bg-white ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(16,24,40,0.08),0_4px_10px_-4px_rgba(16,24,40,0.28)] flex items-center justify-center text-[13px] font-bold text-gray-900 select-none">
      {agentLabel.charAt(0).toUpperCase()}
    </div>
  );
  const introBubble = (
    <motion.div className="flex items-start gap-2.5" variants={itemVariants}>
      <div className="shrink-0 pt-[22px]">{introAvatar}</div>
      <div className="flex-1 min-w-0">
        <div
          className="pl-1 pb-1 text-[12px] font-semibold tracking-[0.01em] text-white [text-shadow:0_1px_1px_rgba(0,0,0,0.05)]"
          style={{
            ...(compact ? { color: "#374151" } : headerTextColor ? { color: headerTextColor } : {}),
            // Secondary, but still legible on a light brand colour (white on
            // peach at 0.72 read faint on flowapt.com): 0.88 + the same soft
            // shadow the greeting carries.
            opacity: 0.88,
          }}
        >
          {agentLabel}
        </div>
        <div
          className={`${CARD.replace(CARD_SHADOW_HOVER, "")} rounded-tl-[6px] px-4 py-3`}
        >
          {renderIntroBody()}
        </div>
      </div>
    </motion.div>
  );
  const introCard = (
    <motion.div
      className={`${CARD.replace(CARD_SHADOW_HOVER, "")} px-5 py-4 ${
        centered ? "text-center" : ""
      }`}
      variants={itemVariants}
    >
      {renderIntroBody()}
    </motion.div>
  );

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const messageDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // The staggered entrance plays once, when the panel opens: the home screen
  // stays mounted underneath every other view (ChatWidget), so coming back to
  // it is a reveal, not a remount, and there is no exit animation here.
  const motionProps = enableAnimations ? { initial: "hidden", animate: "visible" } : {};

  return (
    <motion.div
      ref={rootRef}
      className={compact ? "min-h-0 flex flex-col relative" : "h-full flex flex-col relative"}
      style={{
        background: compact ? "#FFFFFF" : homeBackdrop ?? grainGradientCss(homeColor, panelColor),
        fontSize: "16px",
      }}
      variants={containerVariants}
      {...motionProps}
    >
      {/* Close button */}
      {onClose && (
        <motion.button
          onClick={onClose}
          className={compact
            ? "absolute top-2 right-2 z-10 h-11 w-11 flex items-center justify-center rounded-full hover:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            : "absolute top-4 right-4 z-10 h-8 w-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full text-white"}
          style={compact ? { color: headerTextColor || "#FFFFFF" } : undefined}
          aria-label="Close chat"
          variants={itemVariants}
        >
          <X size={18} />
        </motion.button>
      )}

      {compact && (
        <div ref={headerRef} className="px-5 py-5 pr-16 shrink-0" style={{ background: headerBackdrop ?? homeColor, color: headerTextColor || "#FFFFFF" }}>
          <h2 className="text-[18px] leading-snug font-semibold whitespace-pre-line break-words">
            {greet(isLoggedIn ? `${headerText || "Hi"} ${firstName} 👋` : preloginHeaderText || headerText || "Welcome to FlowIQ", shopifyCustomerFirstName)}
          </h2>
        </div>
      )}

      {/* Bot/user icon — rendered raw (no tile) when iconDisplay.userRaw is true.
          Height is fixed but WIDTH IS NATURAL — a wide wordmark stays wide,
          a square icon stays square. The child <img> is overridden via arbitrary
          child selector to respect the container's height and use auto width. */}
      {!compact && <div
        className={`pl-8 pt-8 pb-4 text-white flex-shrink-0 flex items-center gap-3 ${
          showTeamAvatars ? "pr-24" : "pr-8"
        } ${centered ? "justify-center" : ""}`}
      >
        {showTeamAvatars && avatarsOnLeft && (
          <motion.div variants={itemVariants}>{avatarCluster}</motion.div>
        )}
        {iconDisplay?.userRaw && customIcons?.userWeb ? (
          <motion.div
            className={`${iconDisplay.userLarge ? "h-16" : "h-12"} inline-flex items-center min-w-0 [&>img]:h-full [&>img]:w-auto [&>img]:max-w-full [&>img]:object-contain`}
            variants={itemVariants}
          >
            {customIcons.userWeb}
          </motion.div>
        ) : (
          <motion.div
            className="w-12 h-12 shrink-0 bg-white rounded-xl flex items-center justify-center ring-1 ring-black/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_2px_6px_rgba(16,24,40,0.10),0_10px_22px_-10px_rgba(16,24,40,0.35)] overflow-hidden p-2"
            variants={itemVariants}
          >
            {customIcons?.userWeb ? (
              customIcons.userWeb
            ) : (
              <Bot size={28} style={{ color: primaryColor }} />
            )}
          </motion.div>
        )}
        {showTeamAvatars && !avatarsOnLeft && (
          <motion.div variants={itemVariants}>{avatarCluster}</motion.div>
        )}
      </div>}

      {/* Scrollable content — overscroll-contain stops the scroll chaining
          to the page behind (an iOS pan there desyncs fixed-element taps). */}
      <div className={compact ? "min-h-0 overflow-y-auto overscroll-contain px-5 pt-5" : "flex-1 overflow-y-auto overscroll-contain px-6"}>
        <motion.div
          className={`${compact ? "pb-5 text-gray-700" : "px-2 pb-4 text-white"} ${centered ? "text-center" : ""}`}
          style={!compact && headerTextColor ? { color: headerTextColor } : undefined}
          variants={itemVariants}
        >
          {!compact && <h2
            className={`${greetingClass} font-bold mb-2 [text-shadow:0_1px_1px_rgba(0,0,0,0.05)] whitespace-pre-line`}
          >
            {greet(
              isLoggedIn
                ? `${headerText || "Hi"} ${firstName} 👋`
                : preloginHeaderText || headerText || "Welcome to FlowIQ",
              shopifyCustomerFirstName
            )}
          </h2>}
          <p
            className={compact ? "text-[17px] leading-snug whitespace-pre-line break-words" : `${taglineClass} opacity-95 [text-shadow:0_1px_1px_rgba(0,0,0,0.05)] whitespace-pre-line`}
          >
            {greet(
              isLoggedIn
                ? subheaderText || "How can we help?"
                : preloginSubheaderText || subheaderText || "Say hi 👋",
              shopifyCustomerFirstName
            )}
          </p>
          {/* Intro as body copy on the gradient (home_design.intro_style text) */}
          {showIntro && introStyle === "text" && (
            <div className={`mt-3 max-w-[44ch] ${centered ? "mx-auto" : ""}`}>
              {renderIntroBody()}
            </div>
          )}
        </motion.div>

        <div className="space-y-3 pb-4">
          {/* Intro on a surface — the agent's speech bubble or a white card
              (home_design.intro_style bubble | card) — leads the column. */}
          {showIntro && introStyle === "bubble" && introBubble}
          {showIntro && introStyle === "card" && introCard}

          {/* Recent message card */}
          {!chatEntryHidden && isLoggedIn && recentMessage && (
            <motion.button
              onClick={onPreviousChats}
              className={`${CARD} overflow-hidden text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="px-6 py-3 border-b border-gray-100">
                <span className="text-gray-800 font-semibold text-base">{copyRecentTitle}</span>
              </div>
              <div className="w-full py-4 px-6 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 font-medium truncate text-[15px]">
                    {formatWhatsAppText(
                      getMessageHeader(recentMessage.message) || getMessageDisplayText(recentMessage.message)
                    )}
                  </p>
                  <p className="text-gray-500 text-[13px]">
                    {recentMessage.senderName} • {getTimeAgo(recentMessage.createdAt)}
                  </p>
                </div>
                <ChevronRight
                  className="text-gray-400 w-5 h-5 flex-shrink-0 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
                />
              </div>
            </motion.button>
          )}

          {/* Send message — only until a conversation exists. Once there is a
              recent message, that card IS the way into the chat, so showing
              both would be redundant. */}
          {!chatEntryHidden && !(isLoggedIn && recentMessage) && (
            <motion.button
              onClick={onPreviousChats}
              className={`${CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex flex-col">
                <span className="text-gray-900 font-semibold text-base mb-0.5">{copySendTitle}</span>
                <span className="text-gray-500 text-sm">{copySendSubtitle}</span>
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}

          {/* My Orders — home_cards.orders false hides the card (22 Sep 2026) */}
          {!ordersCardHidden && isLoggedIn && userInfo?.phone && onViewOrders && (
            <motion.button
              onClick={onViewOrders}
              className={`${CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex flex-col">
                <span className="text-gray-900 font-semibold text-base mb-0.5">{copyOrdersTitle}</span>
                <span className="text-gray-500 text-sm">{copyOrdersSubtitle}</span>
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}

          {/* Quick actions, "buttons" display mode — every configured item is
              its own prominent home card (topic-button style). The search
              opener, when on, becomes its own card above them. */}
          {showQuickActions && quickAsButtons && (
            <>
              {quickHeading && (
                <motion.p
                  className="px-1 pt-1 -mb-1 text-[15px] font-semibold text-gray-900"
                  variants={itemVariants}
                >
                  {quickHeading}
                </motion.p>
              )}
              {showHelpSearch && (
                <motion.div className={CARD} variants={itemVariants}>
                  <div className="p-3">
                    <button
                      type="button"
                      onClick={onOpenHelp}
                      aria-label={quickTitle}
                      className="w-full flex items-center gap-3 bg-gray-100/90 rounded-xl px-4 py-3 text-left transition-colors hover:bg-gray-200/70 active:bg-gray-200 cursor-text ring-1 ring-black/[0.04] shadow-[inset_0_1.5px_3px_rgba(16,24,40,0.07)]"
                    >
                      <span className="flex-1 min-w-0 text-[16px] md:text-[15px] font-semibold text-gray-900 truncate">
                        {quickTitle}
                      </span>
                      <Search className="w-5 h-5 text-gray-700 flex-shrink-0" />
                    </button>
                  </div>
                </motion.div>
              )}
              {quickAsGrid ? (
                // Two-column mosaic — compact cards so more topics fit on
                // screen. No chevron (cramped at this size); labels wrap.
                <motion.div
                  className="grid grid-cols-2 gap-3"
                  variants={itemVariants}
                >
                  {quickItems.map((item) => (
                    <motion.button
                      key={item.id || item.label}
                      onClick={() => onQuickAction?.(item)}
                      className={`${CARD} py-3 px-4 text-left group flex flex-col justify-center min-h-[64px]`}
                      {...cardMotion}
                    >
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt=""
                          aria-hidden="true"
                          className="w-9 h-9 rounded-full object-cover ring-1 ring-black/5 mb-2"
                        />
                      )}
                      <span className="flex items-center gap-1.5 text-gray-900 font-semibold text-[14px] leading-snug">
                        {waMarkFor(item, "h-3.5 w-3.5")}
                        <span>{item.label}</span>
                      </span>
                      {item.description && (
                        <span className="text-gray-500 text-[11.5px] leading-snug mt-0.5">
                          {item.description}
                        </span>
                      )}
                    </motion.button>
                  ))}
                </motion.div>
              ) : (
                quickItems.map((item) => (
                  <motion.button
                    key={item.id || item.label}
                    onClick={() => onQuickAction?.(item)}
                    className={`${CARD} py-4 px-6 flex items-center justify-between gap-3 text-left group`}
                    variants={itemVariants}
                    {...cardMotion}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Person card: a circular photo before the label
                          (item.image_url, 22 Sep 2026), e.g. "Speak to Jan". */}
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt=""
                          aria-hidden="true"
                          className="w-11 h-11 rounded-full object-cover ring-1 ring-black/5 flex-shrink-0"
                        />
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="flex items-center gap-1.5 text-gray-900 font-semibold text-base mb-0.5 min-w-0">
                          {waMarkFor(item)}
                          <span className="truncate leading-none">{item.label}</span>
                        </span>
                        {item.description && (
                          <span className="text-gray-500 text-sm truncate">
                            {item.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="text-gray-400 w-5 h-5 flex-shrink-0 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out" />
                  </motion.button>
                ))
              )}
            </>
          )}

          {/* Quick actions — "Search for help" card. Rows are configured per
              widget (chat / info article / WhatsApp / link) and filtered live
              by the search box when enabled. */}
          {showQuickActions && !quickAsButtons && (
            <motion.div
              className={`${CARD.replace(CARD_SHADOW_HOVER, "")} overflow-hidden`}
              variants={itemVariants}
            >
              {showHelpSearch ? (
                // Looks like the search input (bold dark title, magnifier)
                // but clicking it OPENS THE HELP PAGE with the real search
                // box focused — typing never happens on the home screen.
                <div className="px-4 pt-4">
                  <button
                    type="button"
                    onClick={onOpenHelp}
                    aria-label={quickTitle}
                    className="w-full flex items-center gap-3 bg-gray-100/90 rounded-xl px-4 py-3 text-left transition-colors hover:bg-gray-200/70 active:bg-gray-200 cursor-text ring-1 ring-black/[0.04] shadow-[inset_0_1.5px_3px_rgba(16,24,40,0.07)]"
                  >
                    <span className="flex-1 min-w-0 text-[16px] md:text-[15px] font-semibold text-gray-900 truncate">
                      {quickTitle}
                    </span>
                    <Search className="w-5 h-5 text-gray-700 flex-shrink-0" />
                  </button>
                </div>
              ) : (
                <div className="px-6 pt-4">
                  <span className="text-gray-900 font-semibold text-base">
                    {quickTitle}
                  </span>
                </div>
              )}
              <div className="py-2">
                {quickItems.map((item) => (
                  <button
                    key={item.id || item.label}
                    onClick={() => onQuickAction?.(item)}
                    className="w-full px-6 py-3 flex items-center justify-between gap-3 text-left group hover:bg-gray-50"
                  >
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt=""
                        aria-hidden="true"
                        className="w-9 h-9 rounded-full object-cover ring-1 ring-black/5 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="flex items-center gap-1.5 text-gray-500 group-hover:text-gray-900 text-[15px] min-w-0" style={{ transition: "color 200ms ease-out" }}>
                        {waMarkFor(item, "h-3.5 w-3.5")}
                        <span className="truncate">{item.label}</span>
                      </p>
                      {item.description && (
                        <p className="text-gray-400 text-[12px] truncate">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <ChevronRight
                      className="text-gray-400 w-4 h-4 flex-shrink-0 group-hover:text-gray-600"
                      style={{ transition: "color 200ms ease-out" }}
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Rewards (Passes loyalty) — sits ABOVE manage/WhatsApp by design */}
          {onOpenRewards && (
            <motion.button
              onClick={onOpenRewards}
              className={`${CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex flex-col">
                <span className="text-gray-900 font-semibold text-base mb-0.5">{copyRewardsLabel}</span>
                <span className="text-gray-500 text-sm">{copyRewardsSublabel}</span>
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}

          {/* Manage subscription — sits ABOVE the WhatsApp card by design */}
          {onOpenManage && (
            <motion.button
              onClick={onOpenManage}
              className={`${CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex flex-col">
                <span className="text-gray-900 font-semibold text-base mb-0.5">{copyManageLabel}</span>
                <span className="text-gray-500 text-sm">{copyManageSublabel}</span>
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}

          {/* WhatsApp */}
          {whatsapp?.number && (
            <motion.button
              onClick={() => {
                const text = whatsapp.prefill_text ? encodeURIComponent(whatsapp.prefill_text) : "";
                window.open(`https://wa.me/${whatsapp.number}${text ? `?text=${text}` : ""}`, "_blank");
              }}
              className={`${compact ? "w-full bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200" : CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={whatsappIcon}
                  alt="WhatsApp"
                  className={compact ? "w-6 h-6 shrink-0 rounded-[22%] object-contain" : "w-10 h-10 shrink-0 rounded-[22%] object-contain"}
                />
                {copyWhatsappSublabel ? (
                  <div className="flex flex-col min-w-0">
                    <span className="text-gray-900 font-semibold text-base mb-0.5 break-words">{copyWhatsappLabel}</span>
                    <span className="text-gray-500 text-sm break-words">{copyWhatsappSublabel}</span>
                  </div>
                ) : (
                  <span className="text-gray-800 font-semibold text-base break-words min-w-0">{copyWhatsappLabel}</span>
                )}
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}

          {/* Messenger — same card as WhatsApp, opens the Page's m.me chat */}
          {messengerHref && (
            <motion.button
              onClick={() => window.open(messengerHref, "_blank")}
              className={`${compact ? "w-full bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200" : CARD} py-4 px-6 flex items-center justify-between text-left group`}
              variants={itemVariants}
              {...cardMotion}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={messengerIcon}
                  alt="Messenger"
                  className={compact ? "w-6 h-6 shrink-0 rounded-[22%] object-contain" : "w-10 h-10 shrink-0 rounded-[22%] object-contain"}
                />
                {copyMessengerSublabel ? (
                  <div className="flex flex-col min-w-0">
                    <span className="text-gray-900 font-semibold text-base mb-0.5 break-words">{copyMessengerLabel}</span>
                    <span className="text-gray-500 text-sm break-words">{copyMessengerSublabel}</span>
                  </div>
                ) : (
                  <span className="text-gray-800 font-semibold text-base break-words min-w-0">{copyMessengerLabel}</span>
                )}
              </div>
              <ChevronRight
                className="text-gray-400 w-5 h-5 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-out"
              />
            </motion.button>
          )}
        </div>
      </div>

      {/* Footer */}
      <motion.div className={compact ? "text-center pb-4 px-5 flex-shrink-0" : "text-center py-4 px-6 flex-shrink-0"} variants={itemVariants}>
        <p
          className={`${footerTextColor ? "" : compact ? "text-gray-600 " : "text-black "}${compact ? "text-xs" : "text-sm"} font-medium`}
          style={footerTextColor ? { color: footerTextColor } : undefined}
        >
          Powered by{" "}
          <a
            href="https://flowapt.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{
              fontFamily:
                '"all-round-gothic", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
            }}
          >
            flowapt.com
          </a>
        </p>
      </motion.div>
    </motion.div>
  );
};

export default ChatHome;
